import {useCallback, useEffect, useMemo} from 'react';

import * as SearchDAO from 'search/SearchDAO';
import EntityStore from 'stores/EntityStore';
import useAsyncState, {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import lazyInit from 'util/lazyInit';

import {PromptSuggestion} from './PromptSuggestionTypes';

export type GetSuggestions = () => Promise<Array<PromptSuggestion>>;

/**
 * Helper to search for a recently authored file of a given type.
 */
export async function searchForRecentFile(fileType: string): Promise<{id: string; name: string} | null> {
  const user = EntityStore.currentUser();
  if (user == null) {
    return null;
  }
  const rv = await SearchDAO.postSearchRequest({
    url: '/1/api/search',
    data: {
      filters: [
        {key: 'TYPES', operator: 'IS_ONE_OF', value: [fileType]},
        {key: 'AUTHOR', operator: 'IS_ONE_OF', value: [user.handle]},
        {key: 'ARCHIVE_PURPOSES', operator: 'IS_ONE_OF', value: ['NOT_ARCHIVED']},
      ],
      offset: 0,
      limit: 1,
      query: '',
      sorts: [{key: 'modified_at', reverse: true}],
      nextToken: null,
      groupBy: null,
      searchSource: 'llm:PROMPT_SUGGESTIONS',
    },
  });
  if (rv.ids.length === 0) {
    return null;
  }

  const id = rv.ids[0];
  if (fileType === 'entry') {
    const entry = rv.entrySummariesById[id];
    return {id, name: entry.humanId ?? entry.name};
  } else {
    // Assumes this is a bioentity for now
    return {id, name: rv.fileSummariesById[id].name};
  }
}

const getPromptSuggestionCache = lazyInit(() => new Map<string, Array<PromptSuggestion>>());

/**
 * Wrapper around a `GetSuggestions` function that caches the suggestions for the duration of the session.
 *
 * This avoids flashing to a loading state only to show the same suggestion again when re-mounting the
 * welcome screen of the AI sidepanel.
 *
 * @param id a unique identifier for the suggestion, for caching.
 */
export function useCachedResponse(id: string, getSuggestions: GetSuggestions): GetSuggestions {
  return useCallback(async () => {
    const suggestionCache = getPromptSuggestionCache();
    let suggestions = suggestionCache.get(id);
    if (suggestions == null) {
      suggestions = await getSuggestions();
      suggestionCache.set(id, suggestions);
    }
    return suggestions;
  }, [id, getSuggestions]);
}

/**
 * Wraps an async function that gets prompt suggestions, and returns a list of suggestions.
 *
 * @param getSuggestions should be a referentially stable function to avoid re-querying the suggestions on
 *   every render, i.e. avoid inline functions without useCallback.
 *
 * @returns an array of prompt suggestions, or a null value if the suggestions are still loading.
 */
export function useGetSuggestions<T>(getSuggestions: GetSuggestions): Array<PromptSuggestion | null> {
  const {makeCall: getData, currentState: state} = useAsyncState({makeCall: getSuggestions});
  useEffect(() => {
    getData();
  }, [getData]);

  const suggestions = useMemo(() => {
    switch (state.status) {
      case AsyncStatus.SUCCESS:
        return state.data;
      default:
        // Loading.
        return [null];
    }
  }, [state]);
  return suggestions;
}
